import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const sections = [
  { key: 'overview',   label: 'Overview & Roles' },
  { key: 'recent',     label: 'Recent Changes' },
  { key: 'login',      label: 'Login' },
  { key: 'dashboard',  label: 'Dashboard' },
  { key: 'active',     label: 'Active Projects' },
  { key: 'workflow',   label: 'Task Workflow' },
  { key: 'approvals',  label: 'On Approvals' },
  { key: 'completed',  label: 'Completed Projects' },
  { key: 'reports',    label: 'Reports' },
  { key: 'timesheet',  label: 'Time Sheet' },
  { key: 'settings',   label: 'Settings (Admin)' },
];

const Docs = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('overview');

  if (!user || user.role !== 'admin') {
    return (
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Documentation</h2>
        <p className="text-sm text-gray-600">Only administrators can view the documentation.</p>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeSection) {

      /* ─────────────────────────────── RECENT CHANGES ─────────────────────────────── */
      case 'recent':
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800">Recent Changes</h3>
            <p className="text-sm text-gray-700">
              This section summarises the latest integration and workflow updates that were added recently.
            </p>

            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <p className="text-sm font-semibold text-gray-800 mb-2">Integration API endpoints</p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>The external job status and batch-complete routes were moved into a dedicated backend module, <span className="font-mono bg-gray-100 px-1 rounded">integration_routes.py</span>.</li>
                <li><span className="font-medium">GET /job_status/&lt;job_number&gt;</span> now supports response format selection with <span className="font-mono bg-gray-100 px-1 rounded">?format=json</span> or <span className="font-mono bg-gray-100 px-1 rounded">?format=xml</span>.</li>
                <li>The same endpoint also honors the <span className="font-mono bg-gray-100 px-1 rounded">Accept: application/xml</span> header for XML output.</li>
                <li><span className="font-medium">POST /complete_task/</span> remains available for batch task completion from external systems.</li>
              </ul>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <p className="text-sm font-semibold text-gray-800 mb-2">Admin Panel API Requests tab</p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>The Integration API card now shows the base URL, both endpoint URLs, and copy buttons for quick sharing.</li>
                <li>The Job Status example now includes both JSON and XML response samples.</li>
                <li>The docs and UI now reflect the new format selection options so external users know how to request XML.</li>
              </ul>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <p className="text-sm font-semibold text-gray-800 mb-2">Workflow guard updates</p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>Jobs now block task actions when a break has exceeded its allowed limit.</li>
                <li>The task panel shows a blocking overlay until a lead or manager clears the break override.</li>
              </ul>
            </div>
          </div>
        );

      /* ─────────────────────────────── OVERVIEW ─────────────────────────────── */
      case 'overview':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Overview &amp; Roles</h3>
            <p className="text-sm text-gray-700">
              VP Job Tracking is a prepress workflow management system. Projects move through a defined set of
              tasks (Preflight → CMD → Prepress QC → approvals → RIP QC → Epson → Plate &amp; Despatch).
              Each role sees only the tabs and tasks relevant to their team.
            </p>

            <p className="text-sm font-medium text-gray-800 mt-2">Navigation tabs visible by role</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Tab</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Who can see it</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    ['Dashboard',          'All roles'],
                    ['Active Projects',    'All roles'],
                    ['Completed Projects', 'All roles'],
                    ['On Approvals',       'All roles'],
                    ['Notifications',      'All roles'],
                    ['Reports',            'All leads (preflight, CMD, prepress, QC, production, epson), PM, Admin'],
                    ['Time Sheet',         'All roles'],
                    ['Settings',           'Admin only'],
                    ['Docs',               'Admin only'],
                  ].map(([tab, who]) => (
                    <tr key={tab} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">{tab}</td>
                      <td className="px-3 py-2 text-gray-600">{who}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-sm font-medium text-gray-800 mt-3">All available roles</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Role key</th>
                    <th className="px-3 py-2 text-left font-semibold">Display name</th>
                    <th className="px-3 py-2 text-left font-semibold">Typical responsibilities</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    ['preflight',         'Preflight',           'Step 1 – Preflight check'],
                    ['preflight_lead',    'Preflight Lead',      'Preflight team lead; can assign tasks, insert flexible tasks, edit projects'],
                    ['preflight_prepress','Preflight & Prepress', 'Works across both preflight and prepress stages'],
                    ['cmd',               'CMD',                 'CMD task work'],
                    ['cmd_lead',          'CMD Lead',            'CMD team lead; can assign and insert CMD tasks'],
                    ['cmd_qc',            'CMD QC',              'CMD quality check'],
                    ['prepress',          'Prepress',            'Step 4 – Prepress QC (self QC); can insert Recreation, Scanning, Image Export tasks'],
                    ['prepress_lead',     'Prepress Lead',       'Prepress team lead; full prepress task control'],
                    ['qc',                'QC',                  'Step 6 – RIP QC'],
                    ['qc_lead',           'QC Lead',             'QC team lead'],
                    ['epson_lead',        'Epson Lead',          'Step 7 – Epson Printout; no assignment required'],
                    ['production',        'Production',          'Step 8 – Plate and Despatch; no assignment required'],
                    ['production_lead',   'Production Lead',     'Production team lead'],
                    ['pm',                'Project Manager',     'Creates and edits projects; approves/rejects approvals; full project visibility'],
                    ['admin',             'Admin',               'All PM capabilities plus Settings, user management, and audit tools'],
                  ].map(([key, name, desc]) => (
                    <tr key={key} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-blue-700">{key}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">{name}</td>
                      <td className="px-3 py-2 text-gray-600">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-sm text-gray-700 font-medium mt-3">Project complexity levels</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li><span className="font-medium">simple_simple</span> – Quickest jobs (lowest time estimates)</li>
              <li><span className="font-medium">simple</span> – Standard short jobs</li>
              <li><span className="font-medium">medium_simple</span> – Medium complexity</li>
              <li><span className="font-medium">medium</span> – Regular medium jobs</li>
              <li><span className="font-medium">complex</span> – High-effort jobs</li>
              <li><span className="font-medium">super_complex</span> – Very high effort</li>
              <li><span className="font-medium">combined_job</span> – Multi-part combined jobs (highest estimates)</li>
              <li><span className="font-medium">rotary</span> – Auto-assigned when Job Type = "Rotary"; only Prepress QC and RIP QC are active</li>
              <li>Custom complexities can also be added via <span className="font-medium">Settings → Complexity Time Config</span></li>
            </ul>
          </div>
        );

      /* ─────────────────────────────── LOGIN ─────────────────────────────── */
      case 'login':
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800">Login</h3>
            <p className="text-sm text-gray-700">Users sign in with their registered email or AD username and password.</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li><span className="font-medium">Direct login</span> – Email + password stored in MongoDB (hashed).</li>
              <li><span className="font-medium">AD / SSO login</span> – When Active Directory integration is configured, the backend authenticates via LDAP; the user's AD groups are mapped to a system role automatically.</li>
              <li><span className="font-medium">Role loading</span> – On successful login the JWT token is issued; the frontend reads the role from the token and shows only the tabs that role is allowed to see.</li>
              <li><span className="font-medium">Error messages</span> – Invalid credentials, disabled accounts, and AD connection errors return clear text from the backend.</li>
            </ul>
            <p className="text-sm text-gray-700 font-medium mt-2">Admin tips</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>If a user cannot see a tab they should have access to, verify their <span className="font-medium">role</span> in Settings → User Management, then ask them to log out and back in.</li>
              <li>Default password for CSV-imported users is <span className="font-mono bg-gray-100 px-1 rounded">Temp@123</span>. Ask users to change it after first login.</li>
              <li>If a user forgets their password, edit their account in Settings → User Management and set a new password.</li>
            </ul>
          </div>
        );

      /* ─────────────────────────────── DASHBOARD ─────────────────────────────── */
      case 'dashboard':
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800">Dashboard</h3>
            <p className="text-sm text-gray-700">High-level overview of work in progress, personal status, and key KPIs for the selected date range (Today / Week / Month / Custom).</p>

            <p className="text-sm font-medium text-gray-800">Key widgets</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li><span className="font-medium">Active Users card</span> – Shows users currently on shift. Leads see only their team; admin/PM see all.</li>
              <li><span className="font-medium">Project stats</span> – Total, Active, and Completed project counts for the selected period.</li>
              <li><span className="font-medium">Task status breakdown</span> – Counts by status (In Progress, On Hold, On Query, Completed, etc.).</li>
              <li><span className="font-medium">Tasks by stage</span> – How many tasks are at each workflow stage (Preflight, CMD, Prepress QC, …).</li>
              <li><span className="font-medium">Team task status</span> – Per-team breakdown of current task states (visible to leads and above).</li>
              <li><span className="font-medium">Client workflow data</span> – Projects grouped by client and stage (PM/Admin).</li>
              <li><span className="font-medium">Projects by hours chart</span> – Bar/line chart comparing actual vs expected hours.</li>
              <li><span className="font-medium">Completion rate</span> – % of projects completed on time.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Typical usage</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Check at the start of the day to understand open workload and priorities.</li>
              <li>Leads and PMs can use the dashboard during stand-ups to spot bottlenecks (many On Hold, high On Query count, etc.).</li>
              <li>Switch the date range to "Week" or "Month" for trend analysis.</li>
            </ul>
          </div>
        );

      /* ─────────────────────────────── ACTIVE PROJECTS ─────────────────────────────── */
      case 'active':
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800">Active Projects</h3>
            <p className="text-sm text-gray-700">The main operational screen where users work on live jobs.</p>

            <p className="text-sm font-medium text-gray-800">Project list &amp; filters</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li><span className="font-medium">Search</span> – Filter by project title or job code.</li>
              <li><span className="font-medium">Quick-filter tabs</span> – All / My Projects / In Progress / On Approval / On Hold / Paused / On Query / Not Started. Tabs with matching tasks are highlighted.</li>
              <li><span className="font-medium">My Projects scope</span> – For team leads, toggle between "My tasks" and "Team tasks".</li>
              <li><span className="font-medium">Advanced filters</span> – Priority, Status, Current Stage, Job Location, Assigned User, Date range, and Client (multi-select with persistent preference for PM/Admin).</li>
              <li><span className="font-medium">Pagination</span> – 25 items per page by default; adjustable.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Creating a project (PM / Admin / Preflight Lead)</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Click <span className="font-medium">+ New Project</span>. Fill in Title, Client, Job Code, Job Location, Job Nature, Job Type, Priority, Complexity, Deadline, and Description.</li>
              <li>Selecting Job Type = <span className="font-medium">Rotary</span> automatically sets Complexity to <span className="font-mono bg-gray-100 px-1 rounded">rotary</span>.</li>
              <li>The enabled workflow steps auto-populate based on the client's settings and Job Nature (see Workflow section). Epson Printout and Plate &amp; Despatch are opt-in toggles.</li>
              <li>Projects can also be created automatically via the <span className="font-medium">XML Watcher</span> (a background service that reads XML files placed in a watched folder and calls the API).</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Opening a project's tasks</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Click a project row to open its task panel.</li>
              <li>The task panel shows all tasks for the current project sorted by workflow order.</li>
              <li>Each task shows: name, type (standard / approval), status badge, assigned user, start/end time, working time, efficiency %, and an Activity Log button (ℹ).</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Shift controls (top of the task panel)</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li><span className="font-medium">Start Shift / End Shift</span> – Must start shift before working on tasks. End Shift closes the day's time record.</li>
              <li><span className="font-medium">Break types</span> – Lunch (45 min limit), Dinner (15 min), Snacks (15 min).</li>
              <li><span className="font-medium">Idle reasons</span> – Meeting, Call, Training, No Jobs.</li>
              <li><span className="font-medium">15-hour cap</span> – After 15 hours of office time the End Shift button is suppressed; the user must contact admin to close the shift via Time Sheet.</li>
              <li>When offline (shift not started), the shift metrics panel is hidden.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Flexible / insertable tasks</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li><span className="font-medium">Insert CMD</span> – Admin, PM, Preflight Lead, CMD Lead. Blocked if an active CMD task already exists.</li>
              <li><span className="font-medium">Insert Die Creation</span> – Preflight, Preflight Lead, Prepress, Prepress Lead, PM, Admin. Auto-assigned to the current user.</li>
              <li><span className="font-medium">Insert Recreation</span> – Same roles as Die Creation. Regular workers get auto-assigned; leads/PM/Admin leave it unassigned.</li>
              <li><span className="font-medium">Insert Scanning Task</span> – Prepress, Prepress Lead, Preflight &amp; Prepress, PM, Admin.</li>
              <li><span className="font-medium">Insert PI Preparing</span> – Same as Scanning Task.</li>
              <li><span className="font-medium">Insert Image Export</span> – Same as Scanning Task.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Role-based project edit permissions</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Edit project metadata: PM, Admin, Preflight Lead, Preflight.</li>
              <li>Assign a task to a user: all privileged roles (any lead, CMD QC, Prepress, PM, Admin).</li>
              <li>Delete a project: PM, Admin only.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Activity Log</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Click the ℹ button on any task to open its Activity Log showing every time event (Start, Pause, Hold, Resume, End) with timestamp, user, and duration.</li>
            </ul>
          </div>
        );

      /* ─────────────────────────────── WORKFLOW ─────────────────────────────── */
      case 'workflow':
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800">Task Workflow</h3>
            <p className="text-sm text-gray-700">Every project has an ordered set of tasks. Steps are numbered internally; not all steps are active for every job.</p>

            <p className="text-sm font-medium text-gray-800">Default workflow steps</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Step #</th>
                    <th className="px-3 py-2 text-left font-semibold">Task Name</th>
                    <th className="px-3 py-2 text-left font-semibold">Type</th>
                    <th className="px-3 py-2 text-left font-semibold">Assigned team</th>
                    <th className="px-3 py-2 text-left font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    ['1',  'Preflight',               'Standard',  'Preflight team',    'Can be disabled per client'],
                    ['2',  'Single-up approval',       'Approval',  'Preflight team',    'Conditional; requires Preflight to be enabled; PM/Admin approve'],
                    ['—',  'Die creation (flexible)',   'Standard',  'Preflight/Prepress','Inserted on demand; stays next to the active Preflight round'],
                    ['—',  'Recreation (flexible)',     'Standard',  'Preflight/Prepress','Inserted on demand; auto-assigned to regular workers'],
                    ['—',  'CMD (flexible)',            'Standard',  'CMD team',          'Inserted on demand by admin/PM/preflight/CMD leads'],
                    ['—',  'CMD QC (flexible)',         'Standard',  'CMD QC',            'Quality check of CMD output'],
                    ['—',  'Scanning task (flexible)',  'Standard',  'Prepress',          'For scanning-based artwork'],
                    ['—',  'PI preparing (flexible)',   'Standard',  'Prepress',          'Pre-imaging preparation'],
                    ['—',  'Image Export (flexible)',   'Standard',  'Prepress',          'Export to image formats'],
                    ['4',  'Prepress , QC (self QC)',   'Standard',  'Prepress team',     'Core step; always enabled'],
                    ['5',  'Layout approval',           'Approval',  'Prepress team',     'PM/Admin approve; reassignment also reassigns Layout approval'],
                    ['6',  'RIP QC',                   'Standard',  'QC team',           'Always enabled'],
                    ['7',  'Epson Printout',            'Standard',  'Epson Lead',        'Optional; no assignment required; requires QR scan'],
                    ['8',  'Plate and Despatch',        'Standard',  'Production',        'Optional; no assignment required; requires Epson QR (unless Rotary/Corrugation/Notebook)'],
                  ].map(([step, name, type, team, note]) => (
                    <tr key={name} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-gray-700">{step}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">{name}</td>
                      <td className="px-3 py-2 text-gray-600">{type}</td>
                      <td className="px-3 py-2 text-gray-600">{team}</td>
                      <td className="px-3 py-2 text-gray-500">{note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-sm font-medium text-gray-800 mt-3">Job-nature specific workflow defaults</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li><span className="font-medium">Standard</span> – All enabled steps run as configured per client.</li>
              <li><span className="font-medium">Corrugation / Notebook</span> – Preflight (step 1) and Single-up approval (step 2) are skipped by default. Plate &amp; Despatch does not require QR.</li>
              <li><span className="font-medium">Rotary</span> – Only Prepress QC (step 4) and RIP QC (step 6) are active. Complexity is automatically set to "rotary". No QR required for Plate &amp; Despatch.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-3">Task actions</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li><span className="font-medium">Start</span> – Begin work. Records a start time event.</li>
              <li><span className="font-medium">Pause</span> – Temporarily stop (coffee break, interruption). Records a pause event.</li>
              <li><span className="font-medium">Hold</span> – Stop and put the task on hold (waiting for client input, etc.).</li>
              <li><span className="font-medium">Query</span> – Flag the task as needing clarification.</li>
              <li><span className="font-medium">Complete</span> – Mark the task done. Triggers approval notifications if needed. For Epson Printout and Plate &amp; Despatch, a QR scan is required first.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-3">Assignment rules</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>By default only the assigned user can start/control a task.</li>
              <li><span className="font-medium">Epson Printout</span> and <span className="font-medium">Plate &amp; Despatch</span> can be started and completed by any Epson Lead / Production / Production Lead without needing explicit assignment.</li>
              <li>Leads, PM, and Admin can assign any task to any eligible user via the assign (person+) button.</li>
              <li>When Prepress QC is assigned, Layout approval is automatically also assigned to the same user (unless already assigned to someone else).</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-3">QR verification</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Epson Printout requires the user to scan a QR code. The QR must contain the project's job code (minor format differences like "222" vs "Project 222" are normalised).</li>
              <li>Plate &amp; Despatch requires scanning the same QR code that was used for Epson Printout in the same project (for standard job natures).</li>
              <li>QR can be scanned with a physical scanner or entered manually in the input box.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-3">Task complexity override</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Privileged users can change the complexity level of an individual task (e.g. from <span className="font-mono bg-gray-100 px-1 rounded">simple</span> to <span className="font-mono bg-gray-100 px-1 rounded">complex</span>).</li>
              <li>Downgrading complexity requires a written reason, which is stored in the audit log.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-3">Rejection &amp; rework</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>When an approval (Single-up or Layout) is rejected, the PM or Lead selects a reason and optionally tags Internal and/or External error categories.</li>
              <li>The rejection creates a new round of the parent task (e.g. Preflight R2) so rework is tracked separately.</li>
              <li>Error counts are stored on the task and visible in the Completed Projects task detail view and in reports.</li>
            </ul>
          </div>
        );

      /* ─────────────────────────────── ON APPROVALS ─────────────────────────────── */
      case 'approvals':
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800">On Approvals</h3>
            <p className="text-sm text-gray-700">
              This tab shows approval tasks waiting for a PM/Admin decision. Approvals are created automatically
              when a Preflight or Prepress QC task is completed (if the client has approvals enabled).
            </p>

            <p className="text-sm font-medium text-gray-800">Approval types</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li><span className="font-medium">Single-up approval</span> – Created after Preflight is completed (if enabled for the client).</li>
              <li><span className="font-medium">Layout approval</span> – Created after Prepress QC (self QC) is completed.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Tabs within On Approvals</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li><span className="font-medium">Recent</span> – Newly created approvals awaiting action.</li>
              <li><span className="font-medium">Approved</span> – Approvals that were accepted; the project moved to the next stage.</li>
              <li><span className="font-medium">Rejected</span> – Approvals that were rejected; rework is in progress or completed.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Approval process</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>PM or Admin clicks <span className="font-medium">Approve</span> to pass; the project automatically advances to the next workflow stage.</li>
              <li>To reject, click <span className="font-medium">Reject</span>, enter a reason, and optionally select Internal / External error categories. This creates a rework round.</li>
              <li>The approved/rejected status is reflected in the project task list and in Reports.</li>
            </ul>
          </div>
        );

      /* ─────────────────────────────── COMPLETED PROJECTS ─────────────────────────────── */
      case 'completed':
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800">Completed Projects</h3>
            <p className="text-sm text-gray-700">Read-only view of finished jobs for reference, QA, and audits.</p>

            <p className="text-sm font-medium text-gray-800">Search &amp; filters</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Filter by client, job code/title, priority, job location, and completion date range.</li>
              <li>PM/Admin: multi-select client filter is persisted as a user preference.</li>
              <li>Pagination: 25 items per page (adjustable).</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Task details dialog</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Click a project row to open its full task history.</li>
              <li><span className="font-medium">Project Summary</span> – Actual hours, Expected hours (from complexity config), Overall Efficiency %, and Internal/External error counts.</li>
              <li><span className="font-medium">Task table</span> – All tasks with step, name, type, complexity, status, assigned user, start time, end time, working time, and efficiency per task.</li>
              <li><span className="font-medium">Activity Log button (ℹ)</span> – Click on any task row to view the full time-event log (Start, Pause, Resume, End) with timestamps, user, and durations.</li>
              <li><span className="font-medium">Audit Logs section</span> – Full project-level audit trail at the bottom of the dialog.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Typical usage</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Confirm a job is fully completed before billing or dispatch.</li>
              <li>Investigate quality issues using the error counts and error category details on each task.</li>
              <li>Use the Activity Log for a precise timeline of who worked on what and for how long.</li>
            </ul>
          </div>
        );

      /* ─────────────────────────────── REPORTS ─────────────────────────────── */
      case 'reports':
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800">Reports</h3>
            <p className="text-sm text-gray-700">Analytics and Excel exports for management and team leads. Accessible by all leads, PM, and Admin.</p>

            <p className="text-sm font-medium text-gray-800">Available report sections</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>
                <span className="font-medium">User Task Summary by Job Type</span> – Per-user task counts broken down by Job Type for a selected date range.
                Select users, set from/to dates, click View, then Export to download as Excel.
              </li>
              <li>
                <span className="font-medium">User Completed Task Details</span> – Line-by-line export of every completed task: task name, assigned user, project title, client, complexity, actual hours, efficiency, error counts, and error category details.
                Set from/to dates and click Export.
              </li>
              <li>
                <span className="font-medium">Daily Job Information</span> – Booking summary for a given business day (08:00 IST yesterday to 07:59 IST today). Includes job totals, stages, job nature, and job type breakdown.
              </li>
              <li>
                <span className="font-medium">User Time Tracking Summary</span> – Shift, job, break, and idle hours per user. Select period (Today, Week, Month, or Custom), filter by user, click View then Export.
              </li>
              <li>
                <span className="font-medium">Team Utilization &amp; Efficiency</span> – Aggregated metrics per team for a date range. Filter by team. Shows utilization % and efficiency scores.
              </li>
              <li>
                <span className="font-medium">Completed Project Report</span> – Exportable list of completed projects filtered by client and date range.
              </li>
              <li>
                <span className="font-medium">Active Project Report</span> – Exportable snapshot of currently active projects filtered by client and date range.
              </li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Automated email reports (configured in Settings → SMTP)</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li><span className="font-medium">Daily All-Users Time Tracking Report</span> – Sent at a configured IST time; covers the previous business day's hours.</li>
              <li><span className="font-medium">Daily Job Information Email</span> – Business window: yesterday 08:00 IST to today 07:59 IST; summarises bookings.</li>
              <li><span className="font-medium">User Completed Task Details Email</span> – Daily CSV attachment with each user's completed task details.</li>
              <li><span className="font-medium">Weekly Report</span> – Sent on a configured day of the week at a configured IST time.</li>
              <li><span className="font-medium">Monthly Report</span> – Sent on a configured day of the month.</li>
              <li>All schedules are entered in IST; the backend converts them to UTC for scheduling.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Admin tips</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Cross-reference the Task Details report with the Time Tracking Summary to spot high idle + low task output.</li>
              <li>Use Job Type breakdowns to adjust staffing when one job type dominates.</li>
              <li>All-user summary reports are restricted to admin recipients; PMs are excluded unless explicitly listed.</li>
            </ul>
          </div>
        );

      /* ─────────────────────────────── TIME SHEET ─────────────────────────────── */
      case 'timesheet':
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800">Time Sheet</h3>
            <p className="text-sm text-gray-700">Per-user daily time tracking for auditing shifts, breaks, idle, and job time.</p>

            <p className="text-sm font-medium text-gray-800">View modes</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li><span className="font-medium">Daily</span> – Shows today's time entries.</li>
              <li><span className="font-medium">Weekly</span> – Current week totals per day.</li>
              <li><span className="font-medium">Monthly</span> – Current month totals per day.</li>
              <li><span className="font-medium">Custom</span> – Select any from/to date range.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Columns shown per day</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Shift Start / End times</li>
              <li>Office Hours (total time from shift start to end)</li>
              <li>Job Hours (time actively working on tasks)</li>
              <li>Break Hours – sub-columns: Lunch, Dinner, Snacks</li>
              <li>Idle Hours – sub-columns: Meeting, Call, Training, No Jobs</li>
              <li>Utilization % and Efficiency %</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Break limits</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Lunch: 45 minutes maximum</li>
              <li>Dinner: 15 minutes maximum</li>
              <li>Snacks: 15 minutes maximum</li>
              <li>Rows exceeding a break limit are highlighted in red.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Admin / Lead features</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li><span className="font-medium">View other users</span> – Admin, PM, and Team Leads can select any user (or filter by team) and view their time sheet.</li>
              <li><span className="font-medium">Drill into a day</span> – Click the ℹ button on any row to open a full event-by-event activity log for that day (all shift start/end/break/idle events).</li>
              <li><span className="font-medium">Delete an event</span> – Admin can delete individual time events (e.g. a spurious break record) directly from the activity log dialog using the trash icon.</li>
              <li><span className="font-medium">Set End Shift manually</span> – Admin can close an open shift for any user by clicking the End Shift button on the relevant row, entering the correct date and time. This is necessary when a user's shift crossed midnight or they forgot to end it themselves.</li>
              <li><span className="font-medium">15-hour cap</span> – If a live shift exceeds 15 hours, the operator cannot end it themselves; admin must use Time Sheet to close it.</li>
            </ul>

            <p className="text-sm font-medium text-gray-800 mt-2">Typical usage</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Select a user and date range to verify attendance, overtime, and break compliance.</li>
              <li>Drill into days with long idle time and cross-check with the User Task Details report.</li>
              <li>Use "Set End Shift" to correct shifts for users who left without ending their shift.</li>
            </ul>
          </div>
        );

      /* ─────────────────────────────── SETTINGS ─────────────────────────────── */
      case 'settings':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Settings (Admin Panel)</h3>
            <p className="text-sm text-gray-700">Admin-only area with 8 configuration tabs.</p>

            {[
              {
                tab: '0 – User Management',
                items: [
                  'Add, edit, and delete user accounts.',
                  'Fields: Name, Email, AD Username, Phone, Role, Status (Active / Busy / Offline), Password.',
                  'Either Email or AD Username is required; password is required for new direct-login users.',
                  'Filter users by name/email or role; paginated 10 per page.',
                  'Import users in bulk via CSV (Download Sample CSV for the required format). Default password for CSV-imported users is Temp@123.',
                  'AD username enables SSO login when Active Directory integration is configured.',
                ],
              },
              {
                tab: '1 – Teams Management',
                items: [
                  'Create and manage teams (Preflight, CMD, Prepress, QC, Epson, Production).',
                  'Each team has a type, one or more leads, a list of members, and associated production locations.',
                  'Teams are used in Reports → Team Utilization and in the Dashboard team widget.',
                  'Only users with roles matching the team type are available as members/leads.',
                ],
              },
              {
                tab: '2 – Client Management',
                items: [
                  'Add, edit, and delete client records.',
                  'Fields: Name, Email, Phone, Address, Team (links to a team for utilization tracking).',
                  'Per-client workflow toggles: Preflight Enabled and Single-up Approval Enabled.',
                  'If Preflight is disabled, Single-up Approval is also automatically disabled.',
                  'Import clients in bulk via CSV (Download Sample CSV for format).',
                ],
              },
              {
                tab: '3 – Complexity Time Config',
                items: [
                  'Defines the expected time in minutes for each task at each complexity level.',
                  'Complexity levels: simple_simple, simple, medium_simple, medium, complex, super_complex, combined_job, rotary.',
                  'Tasks: Preflight, Single-up approval, Die creation, Recreation, CMD, CMD QC, Prepress QC, Layout approval, Image Export, RIP QC, Epson Printout, Plate and Despatch.',
                  'Click the edit (pencil) icon on a complexity row to open the row editor and set all task times at once.',
                  'Click Add Complexity to create a new custom complexity level (e.g. "Ultra Complex" → key ultra_complex).',
                  'Click Save Configuration to persist changes. Click Reset to Defaults to reload the page and revert to the saved values.',
                  'These estimates are used for Efficiency % calculations in the task view and in reports.',
                ],
              },
              {
                tab: '4 – API Requests',
                items: [
                  'Use the Integration API card to copy the base URL and endpoint URLs for external systems.',
                  'GET /job_status/<job_number> returns the current job summary and task list for a job number.',
                  'The Job Status endpoint supports JSON or XML via ?format=json, ?format=xml, or the Accept: application/xml header.',
                  'POST /complete_task/ batch-completes tasks up to the requested task name and auto-starts the next pending task.',
                  'Use the base URL field to document the external server address that should be used in production.',
                ],
              },
              {
                tab: '5 – Locations & Resources',
                items: [
                  'Manage Production Locations (e.g. "Floor A", "Bangalore Branch").',
                  'Manage Job Nature list (e.g. Standard, Corrugation, Notebook, Rotary) – used in project creation dropdowns.',
                  'Manage Job Type list (e.g. New Artwork, Repeat, Rotary) – used in project creation dropdowns.',
                  'Job Nature values affect default workflow steps; Job Type = "Rotary" auto-sets complexity to rotary.',
                ],
              },
              {
                tab: '6 – Error Categories',
                items: [
                  'Define the error category tags that leads/PM can select when rejecting an approval.',
                  'Categories are split into Internal (production errors) and External (client or external errors).',
                  'Add, edit, and delete categories. Changes take effect immediately in the rejection dialog.',
                  'Error category counts are reported in the Completed Task Details export.',
                ],
              },
              {
                tab: '7 – SMTP Settings',
                items: [
                  'Configure SMTP server host, port, user, password, TLS, sender name, and from address.',
                  'Test connection: enter a test email address and click Test Email.',
                  'Schedule weekly reports: day of the week + IST time.',
                  'Schedule daily reports: IST time.',
                  'Schedule monthly reports: day of month + IST time.',
                  'Daily All-Users Time Tracking Report: toggle enabled, set send time, subject, and comma-separated recipients.',
                  'Daily Job Information Email: toggle enabled, set send time, subject, and recipients.',
                  'User Completed Task Details Email: toggle enabled, set send time, subject, and recipients. Sends a CSV attachment.',
                  'Master email service toggle: must be ON for any scheduled emails to be sent.',
                  'All times are entered in IST; the backend converts to UTC for the scheduler.',
                ],
              },
            ].map(({ tab, items }) => (
              <div key={tab} className="border border-gray-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-gray-800 mb-2">{tab}</p>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  {items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            ))}

            <p className="text-sm font-medium text-gray-800 mt-2">Admin tips</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>After changing report schedules or recipients, send a test email to verify delivery.</li>
              <li>When adding a new client or job type, review the client's workflow toggles and complexity config to ensure reports and constraints behave correctly.</li>
              <li>Audit Logs for each project are accessible from the project detail view (Active Projects or Completed Projects).</li>
            </ul>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Application Documentation</h2>
      <p className="text-sm text-gray-600 mb-4">
        Reference guide covering all tabs, roles, workflow rules, and admin configuration.
      </p>

      <div className="flex flex-wrap gap-2 mb-6">
        {sections.map(section => (
          <button
            key={section.key}
            type="button"
            onClick={() => setActiveSection(section.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              activeSection === section.key
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {section.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        {renderContent()}
      </div>
    </div>
  );
};

export default Docs;

